import axios from 'axios';
import {
  API_AdminGetAppConfig,
  API_AdminGetNonce,
  API_AdminLogin,
  API_AdminMMLogin,
  AdminData,
  API_AdminGetStats,
} from 'shared';

type Response<T> = { data: T };

abstract class APIFetcher {
  protected sendGetRequest = <T>(url: string, params?: any): Promise<T> => {
    return new Promise((resolve, reject) => {
      axios
        .get(url, { params })
        .then((response: Response<T>) => resolve(response.data))
        .catch((err) => reject(err));
    });
  };

  protected sendPostRequest = <T>(url: string, payload?: any): Promise<T> => {
    return new Promise((resolve, reject) => {
      axios
        .post(url, payload)
        .then((response: Response<T>) => resolve(response.data))
        .catch((err) => reject(err));
    });
  };
}

class LoginFetcher extends APIFetcher {
  public getNonce = (address: string) => {
    return this.sendGetRequest<API_AdminGetNonce>('/eh/lemme/ask', {
      address,
    });
  };

  public mmLogin = (address: string, signature: string) => {
    return this.sendPostRequest<API_AdminMMLogin>('/eh/lemme/in', {
      address,
      signature,
    });
  };

  public login = (password: string) => {
    return this.sendPostRequest<API_AdminLogin>('/eh/lemme/inpls', {
      password,
    });
  };

  public logout = () => {
    return this.sendPostRequest('/eh/lemme/go');
  };

  public validateTokens = (token: string, mmToken: string) => {
    return this.sendPostRequest('/eh/lemme/clarify', { token, mmToken });
  };
}

class ConfigFetcher extends APIFetcher {
  public getLiveConfig = () => {
    return this.sendGetRequest<API_AdminGetAppConfig>('/eh/config/live');
  };

  public getAppConfig = () => {
    return this.sendGetRequest<API_AdminGetAppConfig>('/eh/config/app');
  };

  public setLiveConfig = (config: AdminData) => {
    return this.sendPostRequest<API_AdminGetAppConfig>('/eh/config/live', {
      config,
    });
  };

  public setAppConfig = (config: AdminData) => {
    return this.sendPostRequest('/eh/config/app', { config });
  };
}

class StatsFetcher extends APIFetcher {
  public get = () => {
    return this.sendGetRequest<API_AdminGetStats>('/eh/stats/get');
  };
}

export class Fetcher {
  private readonly _login: LoginFetcher;
  private readonly _config: ConfigFetcher;
  private readonly _stats: StatsFetcher;

  constructor() {
    this._login = new LoginFetcher();
    this._config = new ConfigFetcher();
    this._stats = new StatsFetcher();
  }

  public static setMMTokenHeader = (token: string) => {
    axios.defaults.headers.common['x-auth-mm'] = token;
  };

  public static setTokenHeader = (token: string) => {
    axios.defaults.headers.common['x-auth'] = token;
  };

  public static clearHeaders = () => {
    axios.defaults.headers.common['x-auth'] = '';
    axios.defaults.headers.common['x-auth-mm'] = '';
  };

  get login(): LoginFetcher {
    return this._login;
  }

  get config(): ConfigFetcher {
    return this._config;
  }

  get stats(): StatsFetcher {
    return this._stats;
  }
}

const fetcher = new Fetcher();

export default fetcher;
